import { useMutation, useQuery } from "@tanstack/react-query";
import { useIntl } from "react-intl";
import { reopenConversation } from "@/repository/queryFns";
import {
	ReopenEmailResult,
	ReopenEmailStatus,
} from "@/repository/models/message";
import { useMessage } from "@/common/messages";
import { addBreadcrumb, captureError } from "@/common/helpers/sentryHelper";
import {
	CurrentInteractionActions,
	NewInteraction,
	NewInteractionData,
	useCurrentInteractionStore,
} from "@/common/stores/nonpersistingStores/currentInteraction";
import {
	NavigationActions,
	TabType,
	useNavigationStore,
	ToggleTabType,
} from "@/common/stores/persistingStores/navigation";
import { useCallback, useState } from "react";
import { InteractionHistoryListItemDto } from "@/components/organisms/history/types";
import {
	AppCacheActions,
	CacheKey,
	useAppCacheStore,
} from "@/common/stores/persistingStores/app";
import { InteractionType } from "@/common/types";
import {
	getLastAgentNameForInteraction,
	lastHandledAgentNameQueryKey,
} from "@/repository/queryFns/history";
import { TargetSelectionSource } from "@/repository/models/outboundCall";
import {
	useQueuesStore,
	QueuesState,
} from "@/common/stores/nonpersistingStores/queues";
import { useEmailInteractionPreview } from "@/components/organisms/interaction/useEmailInteractionPreview";

export const useHistoryActions = () => {
	const intl = useIntl();
	const { show: showMessage } = useMessage();
	const currentInteractionActions = useCurrentInteractionStore(
		(actions: CurrentInteractionActions) => actions
	);
	const setSelectedTab = useNavigationStore(
		(state: NavigationActions) => state.setSelectedTab
	);

	const setToggledTab = useNavigationStore(
		(state: NavigationActions) => state.setToggledTab
	);

	const appStoreActions = useAppCacheStore((state: AppCacheActions) => state);

	const queues = useQueuesStore((state: QueuesState) => state.queues);

	const { hideEmailPreview } = useEmailInteractionPreview();

	const reopenEmailConversationMutation = useMutation({
		mutationFn: reopenConversation,
		onSuccess: (result: ReopenEmailResult) => {
			let errorMessageId = undefined;
			switch (result.status) {
				case ReopenEmailStatus.Reopened:
				case ReopenEmailStatus.AlreadyOpen:
					appStoreActions.setCache(
						CacheKey.AcceptedInteractionId,
						result.interaction.id
					);
					hideEmailPreview();
					currentInteractionActions.showActive(
						{
							...result.interaction,
							sendType: queues.find((x) => x.id == result.interaction.queueId)
								?.emailSendType,
						},
						true
					);
					setSelectedTab(TabType.Interactions);
					break;
				case ReopenEmailStatus.AlreadyOpenByOther:
					errorMessageId = "History.ReopenEmail.AlreadyOpenByAnotherAgent";
					break;
				case ReopenEmailStatus.OriginalNotHandledByCurrentAgent:
					errorMessageId =
						"History.ReopenEmail.OriginalNotHandledByCurrentAgent";
					break;
				case ReopenEmailStatus.OutboundConversationHasNoEmails:
					errorMessageId =
						"History.ReopenEmail.OutboundConversationHasNoEmails";
					break;
				case ReopenEmailStatus.UnexpectedError:
				default:
					errorMessageId = "History.ReopenEmail.UnexpectedError";
					break;
			}

			if (errorMessageId) {
				showMessage("global", {
					text: intl.formatMessage({ id: errorMessageId }),
					intent: "error",
					duration: 5000,
				});
			}
		},
		onError: (err, data) => {
			captureError(err, "Error reopening email conversation", undefined, {
				conversationId: data,
			});

			showMessage("global", {
				text: intl.formatMessage({ id: "History.ReopenEmail.UnexpectedError" }),
				intent: "error",
				duration: 5000,
			});
		},
	});

	const reopenEmailConversation = useCallback(
		(interactionHistoryItem: InteractionHistoryListItemDto) => {
			addBreadcrumb("ui_interaction", "Reopen email conversation", {
				interactionHistoryItem,
			});
			reopenEmailConversationMutation.mutate(
				interactionHistoryItem.interactionId
			);
		},
		[reopenEmailConversationMutation]
	);

	const openOutboundWorkspace = useCallback(
		(interactionHistoryItem: InteractionHistoryListItemDto) => {
			appStoreActions.removeCaches([CacheKey.CurrentInteractionId]);
			appStoreActions.removeCaches([CacheKey.AcceptedInteractionId]);

			const selectionSource =
				interactionHistoryItem.type == InteractionType.Voicemail
					? TargetSelectionSource.HistoryVoicemail
					: TargetSelectionSource.HistoryCall;

			const newInteractionData: NewInteractionData = {
				name: interactionHistoryItem.name,
				value: interactionHistoryItem.contactInfo,
				selectionSource: selectionSource,
				selectionInteractionHistoryId: interactionHistoryItem.id,
			};

			const newInteraction: NewInteraction = {
				type: InteractionType.Call,
				data: newInteractionData,
			};

			appStoreActions.setCache(CacheKey.OutboundType, InteractionType.Call);
			setToggledTab(ToggleTabType.NewInteraction);

			currentInteractionActions.showNew(newInteraction);
		},
		[currentInteractionActions]
	);

	return {
		reopenEmailConversation,
		openOutboundWorkspace,
	};
};

export const useHistoryHandling = () => {
	const [hoveredMediaId, setHoveredMediaId] = useState<string>("");
	const [hoveredInteractionType, setHoveredInteractionType] =
		useState<InteractionType>(InteractionType.Call);

	const { data: agentName } = useQuery<string>({
		queryKey: [lastHandledAgentNameQueryKey, hoveredMediaId],
		queryFn: () => {
			return getLastAgentNameForInteraction(
				hoveredInteractionType,
				hoveredMediaId
			);
		},
		enabled: hoveredMediaId != "",
	});

	return {
		setHoveredMediaId,
		setHoveredInteractionType,
		agentName,
	};
};
